import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import Layout from './layout/Layout';
import { HashRouter as Router, Route, Routes } from 'react-router';
import WaitLayer from './layout/WaitLayer';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';

import { forceLoginRequest, loginRequest } from './authConfig';
import axios from 'axios';
import accountService from './services/accountService';
import authService, {
  AUTH_MODE_KEY,
  LOCAL_TOKEN_KEY,
} from './services/authService';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

import { Paper, Typography, CircularProgress, Alert } from '@mui/material';
import { Box } from '@mui/system';
import {
  applyBrandToDocument,
  brandAssetUrl,
  brandConfig,
} from './branding/brandConfig';
import { resolveCmsBranding } from './branding/resolveCmsBranding';
import featureFlagsService from './featureFlags/featureFlagsService';
import tenantService from './pages/tenants/tenantService';
import AuthLoginPage from './pages/auth/AuthLoginPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AcceptInvitationPage, {
  PENDING_INVITE_CODE_KEY,
  INVITE_FLOW_DONE_KEY,
} from './pages/invitations/AcceptInvitationPage';
import { ADMIN_MODE_STORAGE_KEY } from './utils/adminMode';

export const LoadingScreen = ({
  message = 'Betöltés...',
}: {
  message?: string;
}) => {
  return (
    <Box
      id="loading-screen"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        margin: '-8px',
        bgcolor: '#f5f5f5',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: '2rem',
          textAlign: 'center',
          maxWidth: '400px',
          width: '100%',
        }}
      >
        <img
          src={brandAssetUrl(brandConfig.assets.logoSvg)}
          alt={brandConfig.logoAlt}
          width="160"
          height="160"
          style={{ marginBottom: '1rem', objectFit: 'contain' }}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <CircularProgress size={40} />
        </Box>
        <Typography variant="h6" component="h1" gutterBottom>
          {message}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            marginTop: '1rem',
          }}
        >
          Kérjük, várjon...
        </Typography>
      </Paper>
    </Box>
  );
};

const ensureAxiosInterceptor = () => {
  if ((axios as any).__cmsAuthInterceptorAttached) {
    return;
  }
  axios.interceptors.request.use((config) => {
    const t = localStorage.getItem(LOCAL_TOKEN_KEY);
    if (t) {
      config.headers.Authorization = `Bearer ${t}`;
    }
    try {
      const adminMode = localStorage.getItem(ADMIN_MODE_STORAGE_KEY);
      if (adminMode === 'saas') {
        config.headers['X-Admin-Scope'] = 'platform';
      } else if (config.headers) {
        delete (config.headers as any)['X-Admin-Scope'];
      }
    } catch {
      /* ignore */
    }
    return config;
  });
  (axios as any).__cmsAuthInterceptorAttached = true;
};

const App = () => {
  const { instance, accounts } = useMsal();
  const [axiosReady, setAxiosReady] = useState(false);
  const [isUserInfoLoaded, setIsUserInfoLoaded] = useState(false);
  const [isRedirectHandled, setIsRedirectHandled] = useState(false);
  const [localAuthTick, setLocalAuthTick] = useState(0);
  const axiosSetupDone = useRef(false);

  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();

  const isLocalAuth =
    authService.isLocalAuth() && authService.hasLocalToken();
  const hasMsalAccount =
    accounts.length > 0 ||
    instance.getAllAccounts().length > 0 ||
    !!instance.getActiveAccount();

  useEffect(() => {
    applyBrandToDocument();
  }, []);

  useEffect(() => {
    authService.registerSessionEndHandler(async (mode) => {
      if (mode === 'local') {
        window.location.hash = '#/';
        window.location.reload();
        return;
      }
      try {
        const account =
          instance.getActiveAccount() ?? instance.getAllAccounts()[0];
        await instance.logoutRedirect({
          account: account ?? undefined,
          postLogoutRedirectUri: '/',
        });
      } catch (e) {
        console.error('MSAL logoutRedirect failed:', e);
        window.location.hash = '#/';
        window.location.reload();
      }
    });
    return () => {
      authService.registerSessionEndHandler(null);
    };
  }, [instance]);

  useEffect(() => {
    const allAccounts = instance.getAllAccounts();
    if (allAccounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(allAccounts[0]);
    }
    if (allAccounts.length > 0) {
      localStorage.setItem(AUTH_MODE_KEY, 'msal');
    }
    setIsRedirectHandled(true);
  }, [instance]);

  const bootWithToken = useCallback(() => {
    ensureAxiosInterceptor();
    setAxiosReady(true);
  }, []);

  const onLocalAuthenticated = useCallback(() => {
    axiosSetupDone.current = true;
    bootWithToken();
    setIsUserInfoLoaded(false);
    setLocalAuthTick((x) => x + 1);
  }, [bootWithToken]);

  useEffect(() => {
    if (!isRedirectHandled || axiosSetupDone.current) {
      return;
    }

    if (authService.isLocalAuth() && authService.hasLocalToken()) {
      axiosSetupDone.current = true;
      void (async () => {
        if (authService.isAccessTokenExpired()) {
          const refreshed =
            authService.hasRefreshToken() &&
            (await authService.refreshSession());
          if (!refreshed) {
            void authService.endExpiredSession();
            return;
          }
        }
        bootWithToken();
      })();
      return;
    }

    let acc = instance.getActiveAccount();
    if (!acc) {
      const allAccounts = instance.getAllAccounts();
      if (allAccounts.length > 0) {
        acc = allAccounts[0];
        instance.setActiveAccount(acc);
      }
    }

    if (!acc) {
      return;
    }

    axiosSetupDone.current = true;
    localStorage.setItem(AUTH_MODE_KEY, 'msal');

    instance
      .acquireTokenSilent({
        ...loginRequest,
        account: acc,
      })
      .then(
        (response) => {
          localStorage.setItem(LOCAL_TOKEN_KEY, response.accessToken);
          localStorage.setItem(AUTH_MODE_KEY, 'msal');
          dispatch({
            type: 'SET_ACCESS_TOKEN',
            payload: response.accessToken,
          });
          return response.accessToken;
        },
        (error) => {
          console.error('Token acquisition error:', error);
          return instance.acquireTokenRedirect({
            ...forceLoginRequest,
          });
        }
      )
      .then((token) => {
        if (typeof token !== 'string') {
          return;
        }
        ensureAxiosInterceptor();
        setAxiosReady(true);
      });
  }, [dispatch, instance, accounts, isRedirectHandled, bootWithToken, localAuthTick]);

  // Keep access token alive (MSAL silent refresh) / detect local JWT expiry.
  useEffect(() => {
    if (!axiosReady) return;

    const timer = setInterval(async () => {
      if (authService.isLocalAuth()) {
        // Refresh ~2 minutes before access JWT expiry (or if already expired).
        if (
          authService.hasRefreshToken() &&
          authService.isAccessTokenExpired(2 * 60 * 1000)
        ) {
          const ok = await authService.refreshSession();
          if (!ok && authService.isAccessTokenExpired()) {
            void authService.endExpiredSession();
          }
        }
        return;
      }

      const currentAcc = instance.getActiveAccount();
      if (!currentAcc) return;
      try {
        const result = await instance.acquireTokenSilent({
          ...loginRequest,
          account: currentAcc,
        });
        localStorage.setItem(LOCAL_TOKEN_KEY, result.accessToken);
        localStorage.setItem(AUTH_MODE_KEY, 'msal');
        dispatch({
          type: 'SET_ACCESS_TOKEN',
          payload: result.accessToken,
        });
      } catch (error) {
        console.error('Token refresh error:', error);
        try {
          await instance.acquireTokenRedirect({
            ...forceLoginRequest,
          });
        } catch (redirectError) {
          console.error('Token re-login redirect failed:', redirectError);
          void authService.endExpiredSession();
        }
      }
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(timer);
    };
  }, [axiosReady, dispatch, instance]);

  useEffect(() => {
    if (!axiosReady) return;

    let cancelled = false;

    const bootSession = async () => {
      setIsUserInfoLoaded(false);
      try {
        if (
          authService.isLocalAuth() &&
          authService.isAccessTokenExpired()
        ) {
          const refreshed =
            authService.hasRefreshToken() &&
            (await authService.refreshSession());
          if (!refreshed) {
            void authService.endExpiredSession();
            return;
          }
        }

        const [flags, userResponse] = await Promise.all([
          featureFlagsService.get(),
          accountService.getUserInfo(),
        ]);
        if (cancelled) return;

        dispatch({ type: 'LOAD_FEATURE_FLAGS', payload: flags });

        if (userResponse?.canceled) {
          return;
        }

        if (userResponse?.hasError) {
          const authFailed =
            userResponse.httpStatus === 401 ||
            !authService.hasLocalToken() ||
            authService.isAccessTokenExpired();
          if (authFailed) {
            void authService.endExpiredSession();
            return;
          }
          enqueueSnackbar((userResponse.errorMessages ?? []).join(','), {
            variant: 'error',
          });
          return;
        }

        dispatch({ type: 'LOAD_USER_INFO', payload: userResponse });

        const tenantId =
          userResponse?.activeTenantId ??
          userResponse?.ActiveTenantId ??
          null;

        if (tenantId) {
          const brandingRes = await tenantService.getBranding(tenantId);
          if (cancelled) return;
          if (!brandingRes.hasError) {
            dispatch({
              type: 'LOAD_TENANT_BRANDING',
              payload: brandingRes.result,
            });
            const resolved = resolveCmsBranding(brandingRes.result);
            document.title = `${resolved.productName} CMS`;
            const themeColor = document.querySelector(
              'meta[name="theme-color"]'
            );
            if (themeColor) {
              themeColor.setAttribute('content', resolved.primaryColor);
            }
          } else {
            dispatch({ type: 'LOAD_TENANT_BRANDING', payload: null });
          }
        } else {
          dispatch({ type: 'LOAD_TENANT_BRANDING', payload: null });
        }
      } finally {
        if (!cancelled) {
          setIsUserInfoLoaded(true);
          try {
            const flowDone = sessionStorage.getItem(INVITE_FLOW_DONE_KEY);
            if (flowDone) {
              sessionStorage.removeItem(INVITE_FLOW_DONE_KEY);
              sessionStorage.removeItem(PENDING_INVITE_CODE_KEY);
            } else {
              const pending = sessionStorage.getItem(PENDING_INVITE_CODE_KEY);
              const hashNow = window.location.hash || '';
              const hasSession =
                authService.hasLocalToken() ||
                localStorage.getItem(AUTH_MODE_KEY) === 'msal';
              // Never bounce an authenticated user back to the invite shell.
              if (
                pending &&
                !hasSession &&
                !hashNow.includes('/invitations/accept')
              ) {
                window.location.hash = `#/invitations/accept?code=${encodeURIComponent(pending)}`;
              }
            }
          } catch {
            // ignore
          }
        }
      }
    };

    bootSession();
    return () => {
      cancelled = true;
    };
  }, [axiosReady, dispatch, enqueueSnackbar, localAuthTick]);

  if (!isRedirectHandled) {
    return <LoadingScreen message="Hitelesítés inicializálása..." />;
  }

  // Preserve invitation code across the login screen (not after a finished accept).
  try {
    const flowDone = sessionStorage.getItem(INVITE_FLOW_DONE_KEY);
    if (flowDone) {
      sessionStorage.removeItem(INVITE_FLOW_DONE_KEY);
      sessionStorage.removeItem(PENDING_INVITE_CODE_KEY);
    } else {
      const hashForInvite = window.location.hash || '';
      const inviteMatch = hashForInvite.match(
        /\/invitations\/accept\?[^#]*code=([^&]+)/i
      );
      if (inviteMatch?.[1]) {
        sessionStorage.setItem(
          PENDING_INVITE_CODE_KEY,
          decodeURIComponent(inviteMatch[1])
        );
      }
    }
  } catch {
    // ignore
  }

  // Public reset-password / invitation-accept without session
  let hash = window.location.hash || '';
  // Restore invite route if history wiped the hash but code was saved (unauthenticated only).
  if (!hash.includes('/invitations/accept') && !isLocalAuth && !hasMsalAccount) {
    try {
      const pending = sessionStorage.getItem(PENDING_INVITE_CODE_KEY);
      if (pending) {
        window.location.hash = `#/invitations/accept?code=${encodeURIComponent(pending)}`;
        hash = window.location.hash;
      }
    } catch {
      // ignore
    }
  }
  if (hash.includes('/reset-password')) {
    return (
      <Router>
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Routes>
      </Router>
    );
  }
  if (hash.includes('/invitations/accept')) {
    return (
      <Router>
        <Routes>
          <Route
            path="/invitations/accept"
            element={
              <AcceptInvitationPage
                onLocalAuthenticated={onLocalAuthenticated}
              />
            }
          />
        </Routes>
      </Router>
    );
  }

  if (!isLocalAuth && !hasMsalAccount) {
    const pendingInvite = (() => {
      try {
        return !!sessionStorage.getItem(PENDING_INVITE_CODE_KEY);
      } catch {
        return false;
      }
    })();

    return (
      <Box>
        {pendingInvite && (
          <Alert
            severity="info"
            sx={{
              maxWidth: 480,
              mx: 'auto',
              mt: 3,
              mb: 0,
            }}
          >
            Meghívót kaptál egy szervezethez. Ha még nincs fiókod, nyisd meg újra
            a meghívó linket — ott jelszót tudsz létrehozni.
          </Alert>
        )}
        <AuthLoginPage onLocalAuthenticated={onLocalAuthenticated} />
      </Box>
    );
  }

  if (!axiosReady || !isUserInfoLoaded) {
    return <LoadingScreen message="Alkalmazás betöltése..." />;
  }

  if (isLocalAuth) {
    return (
      <div>
        <Router>
          <Suspense fallback={<LoadingScreen message="Oldal betöltése..." />}>
            <WaitLayer />
            <Layout />
          </Suspense>
        </Router>
      </div>
    );
  }

  return (
    <div>
      <AuthenticatedTemplate>
        {axiosReady && (
          <Router>
            <Suspense fallback={<LoadingScreen message="Oldal betöltése..." />}>
              <WaitLayer />
              {isUserInfoLoaded && <Layout />}
            </Suspense>
          </Router>
        )}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <AuthLoginPage onLocalAuthenticated={onLocalAuthenticated} />
      </UnauthenticatedTemplate>
    </div>
  );
};

export default App;
